import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Form,
  message,
  Modal,
  Spin,
  Tooltip,
  Row,
  Col,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { confirm } = Modal;

export default function DownloadsAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [form] = Form.useForm();

  // fetch folders
  const fetchFolders = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/downloads");
      if (res.data && res.data.success) {
        const formatted = (res.data.folders || []).map((f) => ({
          key: f.id,
          id: f.id,
          title: f.title,
          category: f.category,
          filesCount: f.files?.length || 0,
          createdAt: f.createdAt,
        }));
        setList(formatted);
      } else {
        setList([]);
        message.error("Failed to load folders");
      }
    } catch (err) {
      console.error(err);
      message.error("Error loading folders");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);
const [currentPage, setCurrentPage] = useState(1);
const pageSize = 10;
  // add folder
  const handleAdd = async (values) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/admin/downloads/folder", values);
      if (res.data && res.data.success) {
        message.success("Folder created");
        form.resetFields();
        setAddModalVisible(false);
        await fetchFolders();
      } else {
        message.error("Failed to create folder");
      }
    } catch (err) {
      console.error(err);
      message.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  // delete folder
  const handleDelete = (id) => {
    confirm({
      title: "Delete this folder?",
      icon: <ExclamationCircleOutlined />,
      content: "All files inside this folder will be deleted.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);
        try {
          const res = await axiosInstance.delete(`/admin/downloads/folder/${id}`);
          if (res.data && res.data.success) {
            message.success("Folder deleted");
            await fetchFolders();
          } else {
            message.error("Failed to delete folder");
          }
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const filtered = list.filter((item) =>
    `${item.title} ${item.category || ""}`
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  const paginatedData = filtered.slice(
  (currentPage - 1) * pageSize,
  currentPage * pageSize
);
  const columns = [
    {
      title: "Sr.No",
      width: 70,
      render: (_, __, index) => <div className="text-center">{index + 1}</div>,
    },
    {
      title: "Folder Name",
      dataIndex: "title",
      render: (t) => <div className="font-medium">{t}</div>,
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (v) => v || "-",
    },
    {
      title: "Files",
      dataIndex: "filesCount",
      width: 100,
      align: "center",
    },
    {
      title: "Actions",
      width: 160,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
         <Upload
  multiple
  showUploadList={false}
  customRequest={async ({ file, onSuccess, onError, onProgress }) => {
    try {
      // antd Upload gives an UploadFile object; real File is in originFileObj
      const rawFile = file?.originFileObj ?? file;
      const formData = new FormData();
      // backend expects key 'files' (array). Append once per file.
      formData.append("files", rawFile);
      formData.append("folderId", record.id);

      // Option A (recommended): do NOT force Content-Type here and let the browser set boundary.
      const res = await axiosInstance.post("/admin/downloads/files", formData, {
        onUploadProgress: (e) => {
          if (e.total > 0 && onProgress) {
            onProgress({ percent: Math.round((e.loaded / e.total) * 100) }, rawFile);
          }
        },
      });

      // Option B (alternative) — if your axios instance is overriding headers, use this:
      // const res = await axiosInstance.post("/admin/downloads/files", formData, {
      //   headers: { "Content-Type": "multipart/form-data" },
      //   onUploadProgress: (e) => { ... }
      // });

      if (res?.data?.success) {
        message.success("Uploaded");
        fetchFolders();
        onSuccess(res.data, rawFile);
      } else {
        const msg = res?.data?.message || "Upload failed";
        message.error(msg);
        onError(new Error(msg));
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error("Upload failed");
      onError(err);
    }
  }}
>
  <Tooltip title="Upload files">
    <Button icon={<UploadOutlined />} size="small" />
  </Tooltip>
</Upload>


          <Tooltip title="Delete folder">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Downloads Manager
            </h1>
            <p className="text-gray-600">
              Manage folders, images and PDF downloads for customers
            </p>
          </div>

         {/* Controls */}
<div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">

    {/* Search */}
    <div className="flex flex-col">
      <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
        <SearchOutlined className="mr-2" />
        Search folders
      </label>
      <Input
        placeholder="Search by name or category..."
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
          setCurrentPage(1); // ✅ reset pagination on search
        }}
        allowClear
        className="h-10 rounded-lg"
      />
    </div>

    {/* Folder Summary */}
    <div className="flex flex-col">
      <label className="text-sm font-medium text-gray-700 mb-2">
        Folder Summary
      </label>
      <div className="h-10 px-4 rounded-lg border border-gray-200 bg-gray-50 flex items-center">
        <span className="text-sm text-gray-700">
          Total: <strong>{filtered.length}</strong> folders
        </span>
      </div>
    </div>

    {/* Create Button */}
    <div className="flex items-end">
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setAddModalVisible(true)}
        className="h-10 bg-blue-600 hover:bg-blue-700 border-blue-600"
      >
        Create Folder
      </Button>
    </div>

  </div>
</div>


          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            <Table
  dataSource={paginatedData}
  columns={columns}
  rowKey="id"
  pagination={{
    current: currentPage,
    pageSize,
    total: filtered.length,
    onChange: (page) => setCurrentPage(page),
    showSizeChanger: false,
  }}
  locale={{
    emptyText: "No folders created yet",
  }}
/>

          </div>
        </div>
      </div>

      {/* Add Folder Modal */}
      <Modal
        title="Create New Folder"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form layout="vertical" form={form} onFinish={handleAdd}>
          <Form.Item
            label="Folder Name"
            name="title"
            rules={[{ required: true, message: "Folder name required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Category" name="category">
            <Input />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>

          <div className="flex justify-end gap-3">
            <Button onClick={() => setAddModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Create
            </Button>
          </div>
        </Form>
      </Modal>
    </Spin>
  );
}
